/**
 * configControls.jsx
 *
 * Shared, presentational config-editing primitives used by both the legacy
 * bespoke AdminPanel sections AND the schema-driven <ConfigRegistry>. Extracting
 * them here removes the per-section duplication and gives the generic registry
 * the exact same look as the hand-coded sections.
 */
import React from 'react';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { EVENT_TYPE_VALUES } from '../lib/configFilters.js';

// Badge style swatches — mirror the .badge.* classes in styles/global.css.
export const BADGE_STYLES = [
  { cssClass: 'sev-1',       label: 'Sev · Yellow' },
  { cssClass: 'sev-2',       label: 'Sev · Saffron' },
  { cssClass: 'sev-3',       label: 'Sev · Flamingo' },
  { cssClass: 'sev-4',       label: 'Sev · Cinnabar' },
  { cssClass: 'sev-5',       label: 'Sev · Maroon' },
  { cssClass: 'status-new',  label: 'Status · Sky' },
  { cssClass: 'status-open', label: 'Status · Blue' },
  { cssClass: 'status-inv',  label: 'Status · Teal' },
  { cssClass: 'status-wait', label: 'Status · Violet' },
  { cssClass: 'status-res',  label: 'Status · Slate' },
  { cssClass: 'status-esc',  label: 'Status · Error' },
  { cssClass: '',            label: 'Default' },
];

export function Badge({ cssClass, label }) {
  return (
    <span className={`badge ${cssClass}`} style={{ pointerEvents: 'none' }}>
      <span className="dot" />
      {label}
    </span>
  );
}

export function SwatchPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {BADGE_STYLES.map(s => (
        <button
          key={s.cssClass}
          type="button"
          title={s.label}
          aria-label={`Badge style: ${s.label}`}
          aria-pressed={value === s.cssClass}
          onClick={() => onChange(s.cssClass)}
          className={`badge swatch ${s.cssClass}${value === s.cssClass ? ' selected' : ''}`}
        />
      ))}
    </div>
  );
}

// ── Event-type availability (shared by Severity / Status / Treatment) ───────────
const EVENT_TYPE_LABELS = { Treatment: 'Treatment', NearMiss: 'Near miss', ClinicIncident: 'Clinic incident' };

export function useEventTypeOptions() {
  const { config } = useAppConfig();
  const list = (config?.eventTypes || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (list.length) return list.map(t => ({ value: t.value, label: t.label || t.value }));
  return EVENT_TYPE_VALUES.map(v => ({ value: v, label: EVENT_TYPE_LABELS[v] || v }));
}

export const eventTypeLabelFor = (options, v) =>
  options.find(o => o.value === v)?.label || EVENT_TYPE_LABELS[v] || v;

export function EventTypeAvailability({ value, onChange }) {
  const options = useEventTypeOptions();
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label style={{ fontSize: 11 }}>Available for event types</label>
      <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
        {options.map(o => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
      <div className="field-hint" style={{ fontSize: 11 }}>
        {selected.length === 0
          ? 'None ticked → available for all event types.'
          : `Shown only for: ${selected.map(v => eventTypeLabelFor(options, v)).join(', ')}.`}
      </div>
    </div>
  );
}

// ── Treatment-type availability (one level below event types: LHR, AWI, …) ─────
export function useTreatmentTypeOptions() {
  const { config } = useAppConfig();
  return (config?.treatmentTypes || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(t => ({ value: t.value, label: t.label || t.value }));
}

export function TreatmentTypeAvailability({ value, onChange }) {
  const options = useTreatmentTypeOptions();
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label style={{ fontSize: 11 }}>Available for treatment types</label>
      <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
        {options.map(o => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
      <div className="field-hint" style={{ fontSize: 11 }}>
        {selected.length === 0
          ? 'None ticked → available for all treatment types.'
          : `Shown only for: ${selected.map(v => options.find(o => o.value === v)?.label || v).join(', ')}.`}
      </div>
    </div>
  );
}

// Renders nothing when unrestricted — the row already carries an "All types"
// event pill; a second always-on pill would just be noise.
export function TreatmentTypePills({ types }) {
  const options = useTreatmentTypeOptions();
  const list = Array.isArray(types) ? types : [];
  if (!list.length) return null;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em',
      color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
    }}>{list.map(v => options.find(o => o.value === v)?.label || v).join(' · ')}</span>
  );
}

export function EventTypePills({ types }) {
  const options = useEventTypeOptions();
  const list = Array.isArray(types) ? types : [];
  const label = list.length === 0 ? 'All types' : list.map(v => eventTypeLabelFor(options, v)).join(' · ');
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em',
      color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}
